import SessionStorage from '@vantiva-sa/homesight_humaui/Utilities/SessionStorage';
import { AppStorageKeys } from '@vantiva-sa/homesight_humaui/Utilities/Constants';

const env = SessionStorage.get(AppStorageKeys.envVars) || {};
const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';

// Builds "https://host:port" from the runtime env, omitting the port when it is the
// protocol default (443) or missing.
const buildUrl = (urlKey, portKey, fallback) => {
  const base = env[urlKey];
  if (!base) return fallback;
  const port = env[portKey];
  if (!port || String(port) === '443') return base;
  return `${base}:${port}`;
};

// There is no dedicated diagnostics backend yet, so the reachability checks target the
// portal's real runtime endpoints (portal, API, notifications WSS, OAuth) from the env.
const authBase = buildUrl('AUTH_URL', 'AUTH_PORT', currentOrigin);
const realm = env.REALM || '';
const authPrefix = env.AUTH_PATH_PREFIX || '';

export const DIAGNOSTICS_ENDPOINTS = {
  portal: currentOrigin,
  api: buildUrl('API_URL', 'API_PORT', currentOrigin),
  apiHealthPath: env.DIAGNOSTICS_API_HEALTH_PATH || '',
  wss: buildUrl('WEBSOCKET_URL', 'WEBSOCKET_PORT', ''),
  oauth: authBase,
  // OIDC discovery endpoint — same server/realm as the token endpoint, but answers 200 to a
  // GET (the token endpoint only allows POST and would log a 405), so it is a clean probe.
  oauthCheckUrl: realm
    ? `${authBase}${authPrefix}/realms/${realm}/.well-known/openid-configuration`
    : authBase,
  // Optional diagnostics backend endpoints — unset until a backend exists, so they no-op.
  iceConfig: env.DIAGNOSTICS_ICE_CONFIG_URL || '',
  healthTelemetry: env.DIAGNOSTICS_HEALTH_URL || '',
  reportTelemetry: env.DIAGNOSTICS_REPORT_URL || '',
  chimeTestUrl: env.DIAGNOSTICS_CHIME_TEST_URL || 'https://aws.github.io/amazon-chime-sdk-js/',
};

// Public STUN fallback so server-reflexive candidates can still be gathered without a
// backend that issues ephemeral TURN credentials.
export const DIAGNOSTICS_ICE_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

// Port labels shown in the details panel, derived from the same runtime env.
export const DIAGNOSTICS_PORTS = {
  portal: '443',
  api: String(env.API_PORT || '443'),
  wss: String(env.WEBSOCKET_PORT || '3563'),
  oauth: String(env.AUTH_PORT || '8443'),
  videoUdp: 'UDP',
  videoTcp: 'TCP',
};

export const DIAGNOSTICS_TIMEOUTS = {
  request: 2500,
  websocket: 2500,
  ice: 8000,
  passiveTotal: 2500,
  videoCallStep: 8000,
  readiness: 8000,
};

// The module is opt-out: it renders unless the tenant explicitly disables it.
export const isDiagnosticsEnabled = (config) => {
  const configured = config?.homesight_feature_config?.diagnostics_enabled;
  if (configured === false) return false;
  if (String(env.DIAGNOSTICS_ENABLED).toLowerCase() === 'false') return false;
  return true;
};
